import { useMutation } from '@apollo/client';
import {
  IMakeSubscriptionQueryResult,
  IPromotionCode,
  PaymentMetadata,
  PaymentMethodSource,
  PaymentMethodType,
} from '@monofront/types';
import {
  GET_CURRENT_PAYMENT_METHOD,
  MAKE_SUBSCRIPTION,
  UPDATE_SUBSCRIPTION,
} from '../queries';
import { useState } from 'react';

interface IUpdateSubscriptionResult {
  updateSubscription: boolean;
}

interface ISubscriptionState {
  isNewSubscription: boolean;
}

interface ISubscriptionParams {
  paymentMethodId: string;
  paymentMethodSource?: PaymentMethodSource;
  paymentMethodType?: PaymentMethodType;
  coupon?: undefined | IPromotionCode;
  financialPaymentId?: string;
  paymentMetadata?: PaymentMetadata;
}

interface IState {
  updateSubscriptionPaymentMethod: (
    params: ISubscriptionParams,
  ) => Promise<ISubscriptionState>;
  subscribe: (params: ISubscriptionParams) => Promise<ISubscriptionState>;
  loading: boolean;
  setPromotionCode: (promotionCode?: IPromotionCode) => void;
}

export const useSubscription = (
  isMember: boolean,
  email?: string | null,
  onSuccess?: ({
    subscriptionId,
    email,
    promotionCode,
    freeTrialPaymentMethod,
  }: {
    subscriptionId?: string;
    email?: string | null;
    promotionCode?: IPromotionCode | null;
    freeTrialPaymentMethod: PaymentMethodType;
  }) => void,
): IState => {
  const [promotionCode, setPromotionCode] = useState<
    undefined | IPromotionCode
  >();
  const [makeSubscriptionRequest, { loading: loadingMakeSubscriptionRequest }] =
    useMutation<IMakeSubscriptionQueryResult>(MAKE_SUBSCRIPTION, {
      refetchQueries: [{ query: GET_CURRENT_PAYMENT_METHOD }],
    });

  const [
    updateSubscriptionRequest,
    { loading: loadingUpdateSubscriptionRequest },
  ] = useMutation<IUpdateSubscriptionResult>(UPDATE_SUBSCRIPTION, {
    refetchQueries: [{ query: GET_CURRENT_PAYMENT_METHOD }],
  });

  const createSubscription = ({
    paymentMethodId,
    paymentMethodSource = PaymentMethodSource.STRIPE,
    paymentMethodType = PaymentMethodType.CB,
    coupon: verifiedCoupon,
    financialPaymentId,
    paymentMetadata,
  }: ISubscriptionParams) =>
    new Promise<ISubscriptionState>((resolve, reject) => {
      makeSubscriptionRequest({
        variables: {
          paymentMethodId: paymentMethodId,
          promotionCode: verifiedCoupon?.id || promotionCode?.id,
          paymentMethodSource,
          financialPaymentId,
          paymentMetadata,
        },
      })
        .then((clientSecretResponse) => {
          if (!clientSecretResponse.data?.makeSubscriptionV3.id) {
            reject('Error during subscription');

            return;
          }

          onSuccess?.({
            subscriptionId: clientSecretResponse.data?.makeSubscriptionV3.id,
            email,
            promotionCode: verifiedCoupon || promotionCode,
            freeTrialPaymentMethod: paymentMethodType,
          });

          resolve({ isNewSubscription: true });
        })
        .catch((err) => {
          reject(err);
        });
    });

  const updateSubscription = ({
    paymentMethodId,
    paymentMethodSource = PaymentMethodSource.STRIPE,
    paymentMethodType: _paymentMethodeType = PaymentMethodType.CB,
    coupon: _verifiedCoupon,
    financialPaymentId,
    paymentMetadata,
  }: ISubscriptionParams) =>
    new Promise<ISubscriptionState>((resolve, reject) => {
      updateSubscriptionRequest({
        variables: {
          paymentMethodId,
          paymentMethodSource,
          financialPaymentId,
          paymentMetadata,
        },
      })
        .then((clientSecretResponse) => {
          if (!clientSecretResponse.data?.updateSubscription) {
            reject('Error during subscription update');

            return;
          }

          resolve({ isNewSubscription: false });
        })
        .catch((err) => {
          reject(err);
        });
    });

  return {
    updateSubscriptionPaymentMethod: updateSubscription,
    subscribe: isMember ? updateSubscription : createSubscription,
    loading: isMember
      ? loadingUpdateSubscriptionRequest
      : loadingMakeSubscriptionRequest,
    setPromotionCode,
  };
};
